import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { Close as CloseIcon, Search } from '@material-ui/icons';
import { gql } from '@apollo/client';
import Icon from '@mdi/react';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { iconList } from '../../../../common/icons/RoleIconList';

const GET_RESOURCES_FOR_CHAT = gql`
  query GetResourcesForChat {
    vm {
      supportCategories {
        supportCategoryId
        title
        icon
        colour
        supportResources {
          supportResourceId
          title
        }
      }
    }
  }
`;

interface ShareResourceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, metadata: Record<string, unknown>) => void;
}

export const ShareResourceDialog = ({ open, onClose, onSubmit }: ShareResourceDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [runQuery, { data, loading }] = useCampfireLazyQuery<any, any>(GET_RESOURCES_FOR_CHAT);

  useEffect(() => {
    if (open) {
      runQuery();
      setSearchTerm('');
    }
  }, [open]);

  const categories = data?.vm?.supportCategories || [];

  const filteredCategories = categories
    .map((cat: any) => ({
      ...cat,
      supportResources: searchTerm.trim()
        ? (cat.supportResources || []).filter((r: any) => r.title.toLowerCase().includes(searchTerm.toLowerCase()))
        : cat.supportResources || [],
    }))
    .filter((cat: any) => cat.supportResources.length > 0);

  const handleSelect = (resource: any) => {
    onSubmit('Shared a resource', {
      type: 'resource_link',
      resourceId: resource.supportResourceId,
    });
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <Typography style={{ fontWeight: 700, fontSize: '1.15rem', color: '#333' }}>Share Resource</Typography>
            <Typography variant='body2' style={{ color: '#999', marginTop: 2 }}>
              Select a resource to share in the conversation
            </Typography>
          </Grid>
          <Grid item>
            <IconButton aria-label='Close' size='small' onClick={handleClose} style={{ marginTop: -4 }}>
              <CloseIcon style={{ fontSize: 20, color: '#999' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16, paddingBottom: 20 }}>
        <TextField
          fullWidth
          variant='outlined'
          placeholder='Search resources...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 16 }}
          InputProps={{
            style: { borderRadius: 10, fontSize: '0.9rem' },
            startAdornment: (
              <InputAdornment position='start'>
                <Search style={{ color: '#bbb', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        {loading && (
          <Box display='flex' justifyContent='center' p={3}>
            <CircularProgress size={24} />
          </Box>
        )}
        <Box style={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredCategories.map((cat: any) => {
            const colour = cat.colour || '#7047EB';
            const iconKey = cat.icon;
            const hasIcon = iconKey && (iconList as any)[iconKey];

            return (
              <Box key={cat.supportCategoryId} style={{ marginBottom: 16 }}>
                <Box display='flex' alignItems='center' style={{ marginBottom: 8, paddingLeft: 4 }}>
                  {hasIcon ? (
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        backgroundColor: `${colour}18`,
                        marginRight: 10,
                        flexShrink: 0,
                      }}
                    >
                      <Icon path={(iconList as any)[iconKey]} size={0.7} color={colour} />
                    </Box>
                  ) : (
                    <Box
                      style={{
                        width: 4,
                        height: 20,
                        borderRadius: 2,
                        backgroundColor: colour,
                        marginRight: 10,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    style={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {cat.title}
                  </Typography>
                </Box>
                <List disablePadding>
                  {cat.supportResources.map((resource: any) => (
                    <ListItem
                      key={resource.supportResourceId}
                      button
                      onClick={() => handleSelect(resource)}
                      style={{
                        borderRadius: 8,
                        marginBottom: 2,
                        paddingLeft: hasIcon ? 42 : 18,
                        paddingTop: 6,
                        paddingBottom: 6,
                      }}
                    >
                      <ListItemText
                        primary={resource.title}
                        primaryTypographyProps={{ style: { fontSize: '0.9rem', fontWeight: 500, color: '#333' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}
          {!loading && filteredCategories.length === 0 && (
            <Box p={3} textAlign='center'>
              <Typography variant='body2' style={{ color: '#999' }}>
                No resources found
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
